import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Clock, CheckCircle, XCircle, Package, RefreshCw, Gift, HelpCircle, ChevronRight, AlertTriangle, ShieldCheck } from 'lucide-react';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { getReturnsByOrder } from '@/lib/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const OrderDashboard = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedOrder = sessionStorage.getItem('currentOrder');
    if (storedOrder) {
      setOrder(JSON.parse(storedOrder));
    }
    
    loadRequests();
  }, [orderNumber]);

  const loadRequests = async () => {
    try {
      const data = await getReturnsByOrder(orderNumber);
      setRequests(data);
    } catch (error) {
      console.error('Error loading requests:', error);
    }
    setIsLoading(false);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'd MMM yyyy, HH:mm', { locale: it });
    } catch {
      return dateString;
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return { label: 'In attesa', color: 'bg-amber-100 text-amber-800', icon: Clock };
      case 'approved':
        return { label: 'Approvata', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle };
      case 'rejected':
        return { label: 'Rifiutata', color: 'bg-red-100 text-red-800', icon: XCircle };
      case 'shipped':
        return { label: 'Spedito', color: 'bg-blue-100 text-blue-800', icon: Package };
      case 'completed':
        return { label: 'Completata', color: 'bg-zinc-100 text-zinc-800', icon: CheckCircle };
      default:
        return { label: status, color: 'bg-zinc-100 text-zinc-800', icon: Clock };
    }
  };

  const getTypeInfo = (type) => {
    switch (type) {
      case 'exchange':
        return { label: 'Cambio', icon: RefreshCw, color: 'text-blue-600' };
      case 'voucher':
        return { label: 'Voucher', icon: Gift, color: 'text-emerald-600' };
      case 'other':
        return { label: 'Altra richiesta', icon: HelpCircle, color: 'text-zinc-600' };
      default:
        return { label: type, icon: HelpCircle, color: 'text-zinc-600' };
    }
  };

  const returnWindow = order?.return_window;
  const canRequest = returnWindow?.can_request !== false; // default true if not set
  
  const handleNewRequest = () => {
    navigate(`/order/${orderNumber}/select`);
  };

  if (isLoading) {
    return (
      <div className="page-container bg-white">
        <Header showBack backTo="/" />
        <main className="flex-1 flex items-center justify-center">
          <p>Caricamento...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="page-container bg-white">
      <Header showBack backTo="/" />
      
      <main className="flex-1 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-3xl md:text-4xl font-normal tracking-tight mb-2">
              Ordine {orderNumber}
            </h1>
            {order && (
              <p className="text-muted-foreground font-light mb-8">
                {order.customer_name} • {order.email}
              </p>
            )}
          </motion.div>

          {/* Return Window Info */}
          {returnWindow && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="mb-6"
            >
              {canRequest ? (
                <div className={cn(
                  "flex items-start gap-3 p-4 border",
                  returnWindow.reason === 'admin_override'
                    ? "bg-purple-50 border-purple-200"
                    : returnWindow.days_remaining <= 3
                      ? "bg-amber-50 border-amber-200"
                      : "bg-emerald-50 border-emerald-200"
                )}>
                  {returnWindow.reason === 'admin_override' ? (
                    <ShieldCheck className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Clock className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className={cn(
                      "text-sm font-medium",
                      returnWindow.reason === 'admin_override'
                        ? "text-purple-800"
                        : returnWindow.days_remaining <= 3
                          ? "text-amber-800"
                          : "text-emerald-800"
                    )}>
                      {returnWindow.message}
                    </p>
                    {returnWindow.days_remaining !== null && returnWindow.days_remaining <= 3 && returnWindow.reason !== 'admin_override' && (
                      <p className="text-xs text-amber-600 mt-1">Affrettati, la finestra sta per chiudersi!</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 border bg-red-50 border-red-200">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">{returnWindow.message}</p>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Previous Requests */}
          {requests.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-8"
            >
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">
                Le tue richieste precedenti
              </p>
              
              <div className="space-y-4">
                {requests.map((request, index) => {
                  const statusInfo = getStatusInfo(request.status);
                  const typeInfo = getTypeInfo(request.request_type);
                  const StatusIcon = statusInfo.icon;
                  const TypeIcon = typeInfo.icon;
                  
                  return (
                    <motion.div
                      key={request.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.15 + index * 0.1 }}
                    >
                      <Link to={`/track/${request.id}`}>
                        <div className="luxury-card p-6 hover:border-primary transition-colors cursor-pointer">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <TypeIcon className={cn("w-5 h-5", typeInfo.color)} />
                              <span className="font-medium">{typeInfo.label}</span>
                            </div>
                            <div className={cn(
                              "inline-flex items-center gap-2 px-3 py-1 text-xs font-medium rounded-full",
                              statusInfo.color
                            )}>
                              <StatusIcon className="w-3 h-3" />
                              {statusInfo.label}
                            </div>
                          </div>
                          
                          <div className="text-sm text-muted-foreground mb-3">
                            <p>Richiesta del {formatDate(request.created_at)}</p>
                            <p>{request.return_items?.length || 0} articol{request.return_items?.length === 1 ? 'o' : 'i'}</p>
                          </div>
                          
                          {/* Show voucher info if approved */}
                          {request.request_type === 'voucher' && request.status === 'approved' && request.gift_card && (
                            <div className="bg-emerald-50 border border-emerald-200 p-3 mb-3">
                              <p className="text-xs text-emerald-600 font-medium">Voucher disponibile</p>
                              <p className="text-lg font-bold text-emerald-700">{request.gift_card.code}</p>
                            </div>
                          )}
                          
                          {/* Show pending voucher info */}
                          {request.request_type === 'voucher' && request.status === 'pending' && (
                            <div className="bg-amber-50 border border-amber-200 p-3 mb-3">
                              <p className="text-xs text-amber-700">In attesa di approvazione</p>
                              <p className="text-sm text-amber-600">Voucher: €{request.voucher_value?.toFixed(2)}</p>
                            </div>
                          )}
                          
                          {/* Show exchange payment status */}
                          {request.request_type === 'exchange' && request.draft_order && (
                            <div className="bg-blue-50 border border-blue-200 p-3 mb-3">
                              <p className="text-xs text-blue-700">
                                {request.status === 'pending' ? 'Pagamento in attesa' : 'Cambio in elaborazione'}
                              </p>
                              <p className="text-sm text-blue-600">Totale: €{request.total_to_pay?.toFixed(2)}</p>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-end text-sm text-primary">
                            <span>Vedi dettagli</span>
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* New Request Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: requests.length > 0 ? 0.3 : 0.1 }}
          >
            {requests.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h2 className="font-heading text-2xl mb-2">Nessuna richiesta precedente</h2>
                <p className="text-muted-foreground mb-8">
                  {canRequest 
                    ? 'Non hai ancora effettuato richieste di reso o cambio per questo ordine'
                    : 'Non è possibile effettuare richieste per questo ordine'
                  }
                </p>
                {canRequest && (
                  <Button 
                    onClick={handleNewRequest}
                    className="luxury-btn-primary inline-flex items-center gap-3"
                    data-testid="new-request-btn"
                  >
                    <Plus className="w-5 h-5" />
                    Nuova Richiesta
                  </Button>
                )}
              </div>
            ) : (
              <div className="luxury-card p-6 text-center border-dashed">
                {canRequest ? (
                  <>
                    <p className="text-muted-foreground mb-4">
                      Hai bisogno di effettuare un'altra richiesta?
                    </p>
                    <Button 
                      onClick={handleNewRequest}
                      className="luxury-btn-primary inline-flex items-center gap-3"
                      data-testid="new-request-btn"
                    >
                      <Plus className="w-5 h-5" />
                      Nuova Richiesta
                    </Button>
                  </>
                ) : (
                  <p className="text-muted-foreground">
                    La finestra per nuove richieste è scaduta per questo ordine.
                  </p>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default OrderDashboard;
